import React, { useCallback, useMemo, useState } from 'react';

import type { MenuOption, Product } from 'generated/graphql';
import type { ReducerActionOptions } from 'types';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';

import { cloneDeep } from 'lodash';
import AddOptionGroup from './AddOptionGroup/AddOptionGroupHandler';
import OptionGroup from './OptionGroup';

import styles from './OptionGroups.module.scss';
import OptionGroupsTestReorder from './OptionGroupsTestReorder';
import { useItemContext } from '../ItemContext';

interface Props {
  item?: Product | null;
  handleChange: (args: ReducerActionOptions, modifiedOption?: MenuOption) => void;
  isTest?: string;
  isSameOnAllApps: boolean; // eslint-disable-line react/no-unused-prop-types
}

const OptionGroups: React.FC<Props> = ({ item, handleChange: handleParentChange, isTest }) => {
  const [openOptionGroups, setOpenOptionGroups] = useState([] as string[]);

  const { currentVenue, productLink } = useItemContext();

  const itemHasSurcharge = useMemo(() => {
    return item?.variablesByApp?.some((variable) => variable?.surcharge && variable.surcharge.amount > 0);
  }, [item]);

  const toggleOptionGroup = (posId: string): void => {
    if (openOptionGroups.includes(posId)) {
      setOpenOptionGroups(openOptionGroups.filter((id) => id !== posId));
    } else {
      setOpenOptionGroups([...openOptionGroups, posId]);
    }
  };

  const isSameOnAllApps = true;
  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, type } = result;

      if (!destination || !item?.options) {
        return;
      }

      const { options } = item;
      const updatedOptions = cloneDeep(options);

      if (type === 'OPTION_GROUP') {
        const [removed] = updatedOptions.splice(source.index, 1);
        updatedOptions.splice(destination.index, 0, removed);
        handleParentChange({
          key: 'options',
          payload: updatedOptions
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const sourceParent = updatedOptions.find((option) => option.posId === source.droppableId)!;

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const destinationParent = updatedOptions.find((option) => option.posId === destination.droppableId)!;

        const [removed] = sourceParent.variants.splice(source.index, 1);
        destinationParent.variants.splice(destination.index, 0, removed);

        const productLinkOption = productLink?.options?.find(
          (productLinkOption) => productLinkOption.posId === destinationParent.posId
        );
        if (!destinationParent.source && productLinkOption) {
          destinationParent.source = productLinkOption.source;
        }

        handleParentChange(
          {
            key: 'options',
            payload: updatedOptions
          },
          destinationParent
        );
      }
    },
    [item, handleParentChange, productLink?.options]
  );

  if (!item || !currentVenue) {
    return null;
  }

  const { options } = item;

  const handleChange = (index: number, option: MenuOption): void => {
    const newOptions = [...options];

    const productLinkOption = productLink?.options?.find(
      (productLinkOption) => productLinkOption.posId === option.posId
    );
    if (!option.source && productLinkOption) {
      option.source = productLinkOption.source;
    }

    newOptions[index] = option;

    handleParentChange(
      {
        key: 'options',
        payload: newOptions
      },
      option
    );
  };

  const handleDelete = (index: number): void => {
    const newOptions = [...options];

    newOptions.splice(index, 1);

    handleParentChange({
      key: 'options',
      payload: newOptions
    });
  };

  const handleAdd = (option: MenuOption): void => {
    handleParentChange(
      {
        key: 'options',
        payload: [...options, option]
      },
      option
    );

    toggleOptionGroup(option.posId);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <ul className={`${styles.OptionGroups}`} data-testid="optionGroups-container">
        <Droppable droppableId="droppable" type="OPTION_GROUP">
          {(provided) => (
            <div ref={provided.innerRef}>
              {options.map((option, index) => {
                return (
                  <OptionGroup
                    handleChange={(option: MenuOption) => handleChange(index, option)}
                    handleDelete={() => handleDelete(index)}
                    optionGroup={option}
                    key={option.mmId}
                    index={index}
                    isSameOnAllApps={isSameOnAllApps}
                    toggleOptionGroup={toggleOptionGroup}
                    isOpen={openOptionGroups.includes(option.posId)}
                  />
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        {!itemHasSurcharge && (
          <li className={`${styles.AddGroup} ${styles.ListItem}`}>
            <AddOptionGroup handleAdd={handleAdd} currentVenue={currentVenue} item={item} />
          </li>
        )}
      </ul>
      {isTest && <OptionGroupsTestReorder onDragEnd={onDragEnd} testType={isTest} />}
    </DragDropContext>
  );
};

export default OptionGroups;
